import { siteConfig } from '@/lib/site-config'

/**
 * A publishable fact. Anything with `verified: false` is held back until
 * someone at Michigan Aviation confirms it, and must never be rendered.
 */
export type Fact = { label: string; value: string; verified: boolean }

/** Single source of truth for posted fuel prices. Leave values null until posted. */
export const fuelPriceConfig: {
  jetA: string | null
  avgas100LL: string | null
  asOf: string | null
} = {
  jetA: null,
  avgas100LL: null,
  asOf: null,
}

export const fuelPriceRows = [
  { label: '100LL', price: fuelPriceConfig.avgas100LL },
  { label: 'Jet-A', price: fuelPriceConfig.jetA },
].filter((row): row is { label: string; price: string } => Boolean(row.price))

export function fuelAsOfLabel(monthOnly = false): string | null {
  const value = fuelPriceConfig.asOf
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const date = new Date(`${value}T12:00:00Z`)
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    ...(monthOnly ? {} : { day: 'numeric' as const }),
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

export const hasFuelPrices = fuelPriceRows.length > 0 && fuelAsOfLabel() !== null

export const fuelPriceCallLine = `Call ${siteConfig.contact.phone} for today\u2019s price.`
export const fuelTaxNote = 'Prices include all taxes.'
export const fuelContractNote = 'Reduced Jet-A pricing is available through contract fuel vendors.'

/** Quick facts for /fuel. Unverified entries are filtered out before render. */
export const fuelFacts: Fact[] = [
  { label: 'Fuel brand', value: siteConfig.fuelBrand, verified: true },
  { label: 'Grades', value: '100LL and Jet-A, full service', verified: true },
  { label: 'Hours', value: siteConfig.hours.lineService, verified: true },
  {
    label: 'Ordering',
    value: `FlightBridge, ${siteConfig.contact.phone}, ${siteConfig.contact.email}`,
    verified: true,
  },
  { label: 'Safety program', value: 'NATA Safety 1st', verified: false },
  {
    label: 'Contract fuel and cards',
    value:
      'Phillips 66 Aviation contract fuel, World Fuel, Everest Fuel, Multi Service Aviation Card, WingPoints Rewards',
    verified: false,
  },
]

export const verifiedFuelFacts = fuelFacts.filter((fact) => fact.verified)

export const contractFuelFact = fuelFacts.find((fact) => fact.label === 'Contract fuel and cards')

/** Fuel quality and safety practices. Unverified entries are filtered out before render. */
export const fuelQualityPractices: { text: string; href?: string; verified: boolean }[] = [
  {
    text: 'Follows Phillips 66 Aviation FBO quality and safety standards',
    href: 'https://www.phillips66.com/aviation/safety',
    verified: false,
  },
  { text: 'Storage tanks and fuel trucks sumped daily before first fueling, with results logged', verified: false },
  { text: 'Filter differential pressure monitored and filters changed on schedule', verified: false },
  { text: 'Aircraft bonded before every fueling', verified: false },
  { text: 'Fuel grade checked against the aircraft and the order before fueling', verified: false },
  { text: 'Line technicians trained through NATA Professional Line Service Training (PLST)', verified: false },
  { text: 'Fuel quality records available on request', verified: false },
]

export const verifiedFuelQualityPractices = fuelQualityPractices.filter((item) => item.verified)

/** Ramp and cold-weather support. Unverified entries are filtered out before render. */
export const rampSupportItems: { text: string; verified: boolean }[] = [
  { text: 'Ground power unit (GPU) available.', verified: true },
  { text: 'Jet-A with Prist', verified: false },
  { text: 'Self-serve fuel', verified: false },
]

export const verifiedRampSupportItems = rampSupportItems.filter((item) => item.verified)

export function fuelPageTitle(): string {
  const date = fuelAsOfLabel(true)
  return date
    ? `Jet-A & 100LL Fuel Prices at KPTK \u2013 ${date}`
    : 'Jet-A & 100LL Fuel at KPTK | Michigan Aviation'
}

export const fuelPageDescription =
  'Phillips 66 100LL and Jet-A at KPTK in Waterford, Michigan. Full-service fueling 5 AM\u201311 PM daily, 24 hours with advance notice.'

/** Price sentence shared by the page body, the FAQ and FAQPage JSON-LD. */
export function fuelPricingSummary(): string {
  if (!hasFuelPrices) return fuelPriceCallLine
  return `As of ${fuelAsOfLabel()}: ${fuelPriceRows
    .map((row) => `${row.label} ${row.price}`)
    .join('; ')}. ${fuelTaxNote} ${fuelContractNote}`
}

const baseFuelFaqs = [
  {
    question: 'What fuel brand does Michigan Aviation sell at KPTK?',
    answer: `${siteConfig.fuelBrand} 100LL and Jet-A, full service.`,
  },
  {
    question: 'What are the fueling hours?',
    answer: `Line service runs ${siteConfig.hours.lineServiceShort}.`,
  },
  {
    question: 'What are the current fuel prices?',
    answer: fuelPricingSummary(),
  },
]

const conditionalFuelFaqs = [
  // Hidden until the contract fuel and card programs are verified.
  ...(contractFuelFact?.verified
    ? [
        {
          question: 'Which fuel cards and contract programs are accepted?',
          answer: `${contractFuelFact.value}.`,
        },
      ]
    : []),
  // Hidden until at least one fuel quality practice is verified.
  ...(verifiedFuelQualityPractices.length > 0
    ? [
        {
          question: 'How is fuel quality checked?',
          answer: `${verifiedFuelQualityPractices.map((item) => item.text).join('. ')}.`,
        },
      ]
    : []),
]

export const fuelFaqs = [
  ...baseFuelFaqs,
  ...conditionalFuelFaqs,
  {
    question: 'Can I arrange engine preheat with 100LL fueling?',
    answer: 'Yes. Add it to your fuel order; preheat is quoted by aircraft type.',
  },
]
